import { useCallback, useMemo, useState } from "react";
import { MapPin, Info, ChevronDown, ChevronUp } from "lucide-react";
import Seatics from "./Seatics";
import UserEventVisualSelector from "./UserEventVisualSelector";
import "./eventSeatMapPanel.css";

/**
 * Venue seat map for event detail — TEVO (Seatics) or community visual picker.
 */
export default function EventSeatMapPanel({
  event,
  ticketData = [],
  pageLoading = false,
  isUserCreatedEvent = false,
  eventId,
  onMapReady,
  onMapTicketsChange,
  hoveredTicketId,
  onTicketHover,
  selectedTicketId,
  onTicketSelect,
  /** "rightColumn" — map fills right pane, minimal chrome (event detail layout) */
  layout = "default",
}) {
  const [mapExpanded, setMapExpanded] = useState(true);
  const [seaticsData, setSeaticsData] = useState([]);

  const containerId = useMemo(
    () => `event-seat-map-${eventId || event?.id || "default"}`,
    [eventId, event?.id]
  );

  const handleSeaticsUpdate = useCallback(
    (tickets) => {
      setSeaticsData(tickets || []);
      onMapTicketsChange?.(tickets || []);
    },
    [onMapTicketsChange]
  );

  const hasTickets = ticketData?.length > 0;
  const canMountMap = !pageLoading && hasTickets && event;

  const isRightColumn = layout === "rightColumn";

  return (
    <div
      className={`event-seat-map-panel ${
        isRightColumn ? "event-seat-map-panel--right-column h-full" : ""
      }`}
    >
      {!isRightColumn && (
      <div className="event-seat-map-panel__header">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-red-600/15 border border-red-600/25 flex items-center justify-center shrink-0">
            <MapPin className="w-4 h-4 text-red-500" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-black uppercase tracking-tight text-white">
              {isUserCreatedEvent ? "Ticket types" : "Venue seat map"}
            </h3>
            <p className="text-xs text-white/50 mt-0.5 leading-relaxed">
              {isUserCreatedEvent
                ? "Pick a ticket category below, then choose quantity in the list."
                : "Click a section on the map to filter listings. Hover tickets to highlight seats."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMapExpanded((v) => !v)}
          className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#374151] text-xs text-white/70 hover:bg-white/5"
        >
          {mapExpanded ? (
            <>
              Hide map <ChevronUp className="w-3.5 h-3.5" />
            </>
          ) : (
            <>
              Show map <ChevronDown className="w-3.5 h-3.5" />
            </>
          )}
        </button>
      </div>
      )}

      <div
        className={`${
          isRightColumn ? "block h-full" : `${mapExpanded ? "block" : "hidden"} lg:block`
        }`}
      >
        <div
          className={`event-seat-map-panel__body ${
            isUserCreatedEvent ? "event-seat-map-panel__body--community" : ""
          }`}
        >
          {!canMountMap ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] px-6 text-center">
              <Info className="w-8 h-8 text-white/20 mb-2" />
              <p className="text-sm text-white/50">
                Seat map will appear when tickets are available.
              </p>
            </div>
          ) : isUserCreatedEvent ? (
            <div className="event-seat-map-panel__map-host p-2 sm:p-4">
              <UserEventVisualSelector
                setSidebarData={handleSeaticsUpdate}
                GroupsEvents={ticketData}
                event={event}
                selectedTicketId={selectedTicketId}
                hoveredTicketId={hoveredTicketId}
                onTicketSelect={onTicketSelect}
              />
            </div>
          ) : (
            <div className="seatics-map-shell seatics-map-shell--map-only event-seat-map-panel__map-host">
              <Seatics
                key={`${containerId}-${ticketData?.length || 0}`}
                containerId={containerId}
                hideTicketList
                suppressInternalLoader
                onMapReady={onMapReady}
                setSidebarData={handleSeaticsUpdate}
                SeaticsData={[]}
                GroupsEvents={ticketData}
                event={event}
              />
            </div>
          )}
        </div>
      </div>

      {/* {!isUserCreatedEvent && seaticsData?.length > 0 && (
        <div className="px-4 py-2.5 border-t border-[#1F2937] bg-[#0F172A]/80 text-xs text-white/50">
          Showing {seaticsData.length} listing
          {seaticsData.length !== 1 ? "s" : ""} for the selected section — pick quantity below.
        </div>
      )} */}
    </div>
  );
}
